/**
 * Top-level React ErrorBoundary. Catches render-time errors anywhere in
 * the tree, logs them to /errorLog, and renders a minimal fallback so
 * the app doesn't crash to a blank screen.
 */

import { Box, Button, Paper, Typography } from '@mui/material';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { logError } from '../services/errorLog';

interface State {
  hasError: boolean;
  message?: string;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, message: err.message };
  }

  componentDidCatch(err: Error, info: ErrorInfo): void {
    void logError({
      surface:
        typeof window !== 'undefined'
          ? `react:${window.location.pathname}`
          : 'react:unknown',
      message: err.message,
      stack: err.stack,
      errorCode: err.name ?? 'react_render',
      inputSnapshot: { componentStack: info.componentStack },
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.default',
            p: 3,
          }}
        >
          <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center' }}>
            <Typography variant="h2" sx={{ mb: 1 }}>
              Midagi läks valesti
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {this.state.message ?? '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
              Viga on logitud admini paneeli. Proovi lehte värskendada.
            </Typography>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Värskenda
            </Button>
          </Paper>
        </Box>
      );
    }
    return this.props.children;
  }
}
